import React, { useState, useEffect } from 'react';
import { 
  DollarSign, TrendingUp, TrendingDown, Users, CreditCard, Server, Globe,
  Activity, Zap, Crown, UserCheck, UserX,
  BarChart3, ArrowUpRight, ArrowDownRight, Wallet, PieChart
} from "lucide-react";
import api from '../../lib/api';
import {
  AreaChart, Area, BarChart, Bar, PieChart as RePie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line
} from 'recharts';

const COLORS = ['#3fb950', '#58a6ff', '#f59e0b', '#a371f7', '#f78166', '#ff6f00'];

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const res = await api.get('/api/admin/analytics/revenue');
      setData(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="loader"></div></div>;
  if (!data) return <p style={{ color: 'var(--text-secondary)', textAlign: 'center' }}>Failed to load analytics</p>;

  const { revenue, users, plans, usage, revenue_trend, top_users } = data;
  const grossMargin = 0.965; // After ~3.5% payment processing fees

  return (
    <div className="page-content" style={{ maxWidth: '100%' }}>
      
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-xl)' }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <BarChart3 size={22} color="var(--accent-primary)" /> Revenue & Analytics
        </h2>
        <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: 0 }}>
          Generated {new Date(data.generated_at).toLocaleString()}
        </p>
      </div>

      {/* KPI Row 1,  Revenue */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
        <KPICard icon={<DollarSign size={22} />} label="Total Revenue" value={`$${(revenue.total || 0).toLocaleString()}`} color="var(--success)" />
        <KPICard icon={<TrendingUp size={22} />} label="This Month" value={`$${(revenue.this_month || 0).toLocaleString()}`} color="var(--accent-primary)" sub={`${revenue.growth_pct >= 0 ? '+' : ''}${revenue.growth_pct}% vs last month`} />
        <KPICard icon={<DollarSign size={22} />} label="Net (after fees)" value={`$${Math.round(revenue.this_month * grossMargin).toLocaleString()}`} color="var(--warning)" sub="~3.5% payment processing" />
        <KPICard icon={<Wallet size={22} />} label="ARPU" value={`$${revenue.arpu}`} color="var(--accent-secondary)" sub="Avg revenue per paying user" />
      </div>

      {/* KPI Row 2,  Users */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
        <KPICard icon={<Users size={22} />} label="Total Users" value={users.total} color="var(--info)" sub={`${users.new_this_month} new this month`} />
        <KPICard icon={<Crown size={22} />} label="Paying Users" value={users.paying} color="var(--success)" sub={`${users.conversion_rate}% conversion`} />
        <KPICard icon={<UserCheck size={22} />} label="Active (7d)" value={users.active_7d} color="var(--accent-primary)" />
        <KPICard icon={<UserX size={22} />} label="Churned" value={users.churned_this_month} color="var(--error)" sub={`${users.churn_rate}% churn rate`} />
        <KPICard icon={<Activity size={22} />} label="Searches (MTD)" value={(usage?.searches_this_month || 0).toLocaleString()} color="var(--accent-secondary)" />
        <KPICard icon={<Zap size={22} />} label="Total Leads" value={(usage?.total_leads || 0).toLocaleString()} color="var(--warning)" />
      </div>

      {/* Profit & Expenses */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
        <KPICard icon={<DollarSign size={22} />} label="Net Profit (MTD)" value={`$${(data.profit?.net || 0).toLocaleString()}`} color={data.profit?.net >= 0 ? 'var(--success)' : 'var(--error)'} sub={`${data.profit?.margin_pct || 0}% margin`} />
        <KPICard icon={<Server size={22} />} label="Infrastructure" value={`$${data.expenses?.infrastructure || 0}`} color="var(--text-secondary)" sub="Hetzner CX33" />
        <KPICard icon={<Globe size={22} />} label="Domain & Services" value={`$${data.expenses?.domain || 0}`} color="var(--text-secondary)" sub="Annual domain / 12" />
        <KPICard icon={<CreditCard size={22} />} label="Payment Fees" value={`$${(data.fees?.payment_processing || 0).toLocaleString()}`} color="var(--warning)" sub="Crypto 1%" />
      </div>

      {/* Expense Breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-xl)' }}>
          <h4 style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, marginBottom: 16 }}>💰 Profit Breakdownn (This Month)</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: 'var(--text-secondary)' }}>Revenue</span>
              <span style={{ color: 'var(--success)', fontWeight: 600 }}>+${(data.profit?.gross || 0).toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: 'var(--text-secondary)' }}>Infrastructure</span>
              <span style={{ color: 'var(--error)', fontWeight: 500 }}>-${data.expenses?.infrastructure || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: 'var(--text-secondary)' }}>Payment Fees</span>
              <span style={{ color: 'var(--error)', fontWeight: 500 }}>-${(data.fees?.payment_processing || 0).toLocaleString()}</span>
            </div>
            <div style={{ height: 1, background: 'var(--border-primary)' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15 }}>
              <span style={{ color: 'var(--text-primary)', fontWeight: 700 }}>Net Profit</span>
              <span style={{ color: (data.profit?.net || 0) >= 0 ? 'var(--success)' : 'var(--error)', fontWeight: 700 }}>
                ${(data.profit?.net || 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-xl)' }}>
          <h4 style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, marginBottom: 16 }}>📊 Key Metrics</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              ['Gross Margin (after fees)', `${Math.round((1 - (data.fees?.payment_processing || 0) / Math.max(data.profit?.gross || 1, 1)) * 100)}%`],
              ['Profit per Paying User', `$${data.revenue?.arpu ? Math.round((data.profit?.net || 0) / Math.max(data.users?.paying || 1, 1)) : 0}`],
              ['Monthly Expenses', `$${data.expenses?.total || 0}`],
              ['Break-even Users', `${Math.ceil((data.expenses?.total || 0) / Math.max(data.revenue?.arpu || 1, 1))} paying users`],
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
                <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row 1,  Revenue Trend + User Growth */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 'var(--space-xl)', marginBottom: 'var(--space-xl)' }}>
        <ChartCard title="Revenue Trend (6 Months)" icon={<DollarSign size={16} />}>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={revenue_trend || []}>
              <defs>
                <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3fb950" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3fb950" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" />
              <XAxis dataKey="month" stroke="var(--text-tertiary)" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
              <YAxis stroke="var(--text-tertiary)" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 8, color: 'var(--text-primary)' }} />
              <Area type="monotone" dataKey="revenue" stroke="#3fb950" fill="url(#colorRev)" name="Revenue" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Revenue by Payment Method" icon={<CreditCard size={16} />}>
          <ResponsiveContainer width="100%" height={280}>
            <RePie>
              <Pie data={revenue.payment_split || []} dataKey="amount" nameKey="method" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3}>
                {(revenue.payment_split || []).map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 8, color: 'var(--text-primary)' }} />
            </RePie>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts Row 2,  Plan Distribution + Top Users */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 'var(--space-xl)', marginBottom: 'var(--space-xl)' }}>
        <ChartCard title="Plan Distribution" icon={<Crown size={16} />}>
          <ResponsiveContainer width="100%" height={280}>
            <RePie>
              <Pie data={plans || []} dataKey="count" nameKey="plan" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3}>
                {(plans || []).map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 8, color: 'var(--text-primary)' }} />
            </RePie>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="User Breakdown" icon={<Users size={16} />}>
          <ResponsiveContainer width="100%" height={280}>
            <RePie>
              <Pie data={[
                { name: 'Free', value: users.free },
                { name: 'Paying', value: users.paying },
              ]} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3}>
                <Cell fill="var(--text-tertiary)" />
                <Cell fill="var(--success)" />
              </Pie>
              <Tooltip contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 8, color: 'var(--text-primary)' }} />
            </RePie>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top Users Table */}
      {top_users && top_users.length > 0 && (
        <div style={{
          background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
          borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)',
          boxShadow: 'var(--shadow-sm)',
        }}>
          <h3 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Crown size={16} /> Top Paying Users (This Month)
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-primary)' }}>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Company</th>
                  <th style={thStyle}>Plan</th>
                  <th style={thStyle}>Total Searches</th>
                  <th style={thStyle}>Joined</th>
                </tr>
              </thead>
              <tbody>
                {top_users.map((u, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                    <td style={tdStyle}>{u.email}</td>
                    <td style={tdStyle}>{u.company_name || ', '}</td>
                    <td style={tdStyle}><span style={{ padding: '2px 10px', borderRadius: 10, background: 'var(--accent-glow)', color: 'var(--accent-primary)', fontWeight: 600, fontSize: 11, textTransform: 'capitalize' }}>{u.plan}</span></td>
                    <td style={tdStyle}>{u.searches}</td>
                    <td style={tdStyle}>{new Date(u.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function KPICard({ icon, label, value, color, sub }) {
  return (
    <div style={{
      background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
      borderRadius: 'var(--radius-lg)', padding: 'var(--space-xl)',
      boxShadow: 'var(--shadow-sm)', transition: 'all 0.2s ease', cursor: 'default',
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ color }}>{icon}</span>
        {sub && sub.includes('+') && <ArrowUpRight size={14} color="var(--success)" />}
        {sub && sub.includes('-') && <ArrowDownRight size={14} color="var(--error)" />}
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1, marginTop: 8 }}>{value}</div>
      <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 4 }}>{label}</div>
      {sub && <div style={{ color: 'var(--text-tertiary)', fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ChartCard({ title, icon, children }) {
  return (
    <div style={{
      background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
      borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)',
      boxShadow: 'var(--shadow-sm)',
    }}>
      <h3 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        {icon} {title}
      </h3>
      {children}
    </div>
  );
}

const thStyle = { padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 14px', color: 'var(--text-primary)', whiteSpace: 'nowrap' };
